import t from "@/lang/lang";
import { useHtzReportCreateMutation } from "@/services/backend/htz/report/api/report-api";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { Slot } from "radix-ui";
import { toast } from "sonner";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { BranchSelectFrom } from "@/services/backend/branch/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";

export function CreateReportButton({
  processId,
  onSuccess,
  trigger,
}: {
  processId: string;
  onSuccess?: (reportId: UUID) => void;
  trigger?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { data: process } = useProcessesProcessShowQuery({ id: processId });
  const [create, { isLoading, error, reset }] = useHtzReportCreateMutation();

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error, false));
      reset();
    }
  }, [error, reset]);

  const disabled =
    isLoading || !process || branchId === "" || serviceObjectId === "";

  const doCreate = () => {
    if (disabled) {
      return;
    }
    const id = v4();
    create({
      id,
      processId,
      companyId: process!.companyId,
      branchId,
      customerId: process!.customerId!,
      serviceObjectId,
    })
      .unwrap()
      .then(() => {
        setOpen(false);
        onSuccess?.(id);
        reset();
      });
  };

  if (branches.length === 1 && serviceObjects.length === 1) {
    if (trigger) {
      return <Slot.Root onClick={doCreate}>{trigger}</Slot.Root>;
    }
    return (
      <Button onClick={doCreate} disabled={disabled} size="sm">
        <Plus />
        {t("Bericht")}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button disabled={disabled} size="sm">
            <Plus />
            {t("Bericht")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader title={t("Bericht erstellen")} />
        {branches.length !== 1 && (
          <Field>
            <FieldLabel>{t("Niederlassung")}</FieldLabel>
            <BranchSelectFrom
              branchIds={branches}
              value={branchId}
              onValueChange={setBranchId}
            />
          </Field>
        )}
        {serviceObjects.length !== 1 && (
          <Field>
            <FieldLabel>{t("Service Objekt")}</FieldLabel>
            <ServiceObjectSelectFrom
              serviceObjectIds={serviceObjects}
              customerId={process?.customerId}
              value={serviceObjectId}
              onValueChange={setServiceObjectId}
            />
          </Field>
        )}
        <CommonDialogFooter
          isLoading={isLoading}
          disabled={disabled}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}
